import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { clientsAPI } from '../lib/clientsAPI';
import api from '../lib/api';
import { ArrowLeft, ArrowRight, CheckCircle, Building2, FileText, Users, Briefcase, ClipboardCheck, Home } from 'lucide-react';
import { RepeatableFieldArray } from '../components/forms/RepeatableFieldArray';

// Name capture is entity-aware: individuals/sole traders give First + Last name;
// companies/trusts/SMSFs give a single Business Name. display_name is composed from these.
const step1Schema = z.object({
  entity_type: z.enum(['company', 'trust', 'smsf', 'sole_trader', 'individual']),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  business_name: z.string().optional(),
}).superRefine((data, ctx) => {
  const isIndividual = data.entity_type === 'individual' || data.entity_type === 'sole_trader';
  if (isIndividual) {
    if (!data.first_name?.trim()) ctx.addIssue({ code: 'custom', path: ['first_name'], message: 'First name is required' });
    if (!data.last_name?.trim()) ctx.addIssue({ code: 'custom', path: ['last_name'], message: 'Last name is required' });
  } else if (!data.business_name || data.business_name.trim().length < 2) {
    ctx.addIssue({ code: 'custom', path: ['business_name'], message: 'Business name must be at least 2 characters' });
  }
});

const step2Schema = z.object({
  abn: z.string().regex(/^\d{11}$/, 'ABN must be 11 digits').optional().or(z.literal('')),
  acn: z.string().regex(/^\d{9}$/, 'ACN must be 9 digits').optional().or(z.literal('')),
  tfn: z.string().regex(/^\d{9}$/, 'TFN must be 9 digits').optional().or(z.literal('')),
  date_of_birth: z.string().optional(),
  address: z.string().optional(),
  gst_registered: z.boolean(),
  bas_cycle: z.string().optional(),
});

const step3Schema = z.object({
  primary_contact_name: z.string().min(2, 'Name required'),
  primary_contact_email: z.string().email('Valid email required'),
  primary_contact_phone: z.string().min(10, 'Valid phone required'),
});

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1
    display_name: '',
    first_name: '',
    last_name: '',
    business_name: '',
    entity_type: 'company',
    // Step 2
    abn: '',
    acn: '',
    tfn: '',
    date_of_birth: '',
    address: '',
    gst_registered: false,
    bas_cycle: 'quarterly',
    // Step 3
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
    additional_contacts: [],
    // Step 3 - Financial Details
    bank_accounts: [],
    // Step 4 - Entity-Specific People
    directors: [],
    shareholders: [],
    trustees: [],
    beneficiaries: [],
    smsf_members: [],
    // Step 5 (was Step 4)
    services: [],
  });

  // Individuals & sole traders have no directors/shareholders/trustees, so the
  // Entity Details step (4) is skipped entirely for them.
  const isIndividual = formData.entity_type === 'individual' || formData.entity_type === 'sole_trader';

  const allSteps = [
    { number: 1, title: 'Basic Information', icon: Building2, description: 'Client name and type' },
    { number: 2, title: 'Tax Details', icon: FileText, description: 'ABN, ACN, TFN, and address' },
    { number: 3, title: 'Financial Details', icon: FileText, description: 'Bank account information' },
    { number: 4, title: 'Entity Details', icon: Users, description: 'Directors, shareholders, trustees' },
    { number: 5, title: 'Key Contacts', icon: Users, description: 'Primary contact' },
    { number: 6, title: 'Services', icon: Briefcase, description: 'Select services needed' },
    { number: 7, title: 'Rental Properties', icon: Home, description: 'Investment properties (optional)' },
    { number: 8, title: 'Review & Complete', icon: ClipboardCheck, description: 'Confirm and finish' },
  ];
  const steps = allSteps.filter((s) => !(s.number === 4 && isIndividual));
  const totalSteps = steps.length;
  const currentStepObj = steps.find((s) => s.number === currentStep) || allSteps.find((s) => s.number === currentStep);
  const currentPosition = Math.max(1, steps.findIndex((s) => s.number === currentStep) + 1);
  const progress = (currentPosition / totalSteps) * 100;

  // Step 1 Form
  const step1Form = useForm({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      entity_type: formData.entity_type,
      first_name: formData.first_name,
      last_name: formData.last_name,
      business_name: formData.business_name,
    },
  });

  // Step 2 Form
  const step2Form = useForm({
    resolver: zodResolver(step2Schema),
    defaultValues: {
      abn: formData.abn,
      acn: formData.acn,
      gst_registered: formData.gst_registered,
      bas_cycle: formData.bas_cycle,
    },
  });

  // Step 3 Form
  const step3Form = useForm({
    resolver: zodResolver(step3Schema),
    defaultValues: {
      primary_contact_name: formData.primary_contact_name,
      primary_contact_email: formData.primary_contact_email,
      primary_contact_phone: formData.primary_contact_phone,
    },
  });

  const handleStep1Submit = async (data) => {
    setLoading(true);
    try {
      const isIndividual = data.entity_type === 'individual' || data.entity_type === 'sole_trader';
      // Compose the canonical display_name from the entity-aware fields.
      const display_name = isIndividual
        ? `${data.first_name.trim()} ${data.last_name.trim()}`.trim()
        : data.business_name.trim();

      const response = await clientsAPI.create({
        display_name,
        entity_type: data.entity_type,
        first_name: isIndividual ? data.first_name.trim() : null,
        last_name: isIndividual ? data.last_name.trim() : null,
      });

      setClientId(response.data.id);
      setFormData({ ...formData, ...data, display_name });
      
      // Update onboarding status
      await clientsAPI.updateOnboarding(response.data.id, {
        current_step: 2,
        completed_steps: [1],
        data: { step1: data },
      });
      
      setCurrentStep(2);
      toast.success('Client created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async (data) => {
    setLoading(true);
    try {
      // Update client with business details
      await clientsAPI.update(clientId, {
        abn: data.abn || null,
        acn: data.acn || null,
        tfn_encrypted: data.tfn || null,
        date_of_birth: data.date_of_birth || null,
        address: data.address || null,
        gst_registered: data.gst_registered,
        bas_cycle: data.gst_registered ? data.bas_cycle : null,
      });
      
      setFormData({ ...formData, ...data });
      
      // Update onboarding status
      await clientsAPI.updateOnboarding(clientId, {
        current_step: 3,
        completed_steps: [1, 2],
        data: { step1: formData, step2: data },
      });
      
      setCurrentStep(3);
      toast.success('Business details saved');
    } catch (error) {
      toast.error('Failed to save business details');
    } finally {
      setLoading(false);
    }
  };

  const handleStep3Submit = async (data) => {
    setLoading(true);
    try {
      // Save bank accounts
      for (const account of data.bank_accounts) {
        await api.post(`/api/clients/${clientId}/bank-accounts`, account);
      }
      
      setFormData({ ...formData, ...data });
      
      // Individuals/sole traders skip Entity Details (step 4) -> straight to Key Contacts (5).
      const nextStep = isIndividual ? 5 : 4;
      await clientsAPI.updateOnboarding(clientId, {
        current_step: nextStep,
        completed_steps: [1, 2, 3],
        data: { ...formData, step3: data }
      });

      setCurrentStep(nextStep);
      toast.success('Financial details saved');
    } catch (error) {
      toast.error('Failed to save financial details');
    } finally {
      setLoading(false);
    }
  };

  const handleStep4Submit = async (data) => {
    setLoading(true);
    try {
      // Save entity-specific data based on type
      const entityType = formData.entity_type;
      
      if (entityType === 'company') {
        // Save directors
        for (const director of data.directors) {
          await api.post(`/api/clients/${clientId}/directors`, director);
        }
        // Save shareholders
        for (const shareholder of data.shareholders) {
          await api.post(`/api/clients/${clientId}/shareholders`, shareholder);
        }
      } else if (entityType === 'trust') {
        // Save trustees and beneficiaries
        for (const trustee of data.trustees) {
          await api.post(`/api/clients/${clientId}/trustees`, trustee);
        }
        for (const beneficiary of data.beneficiaries) {
          await api.post(`/api/clients/${clientId}/beneficiaries`, beneficiary);
        }
      } else if (entityType === 'smsf') {
        // Save SMSF members
        for (const member of data.smsf_members) {
          await api.post(`/api/clients/${clientId}/smsf-members`, member);
        }
      }
      
      setFormData({ ...formData, ...data });
      
      await clientsAPI.updateOnboarding(clientId, {
        current_step: 5,
        completed_steps: [1, 2, 3, 4],
        data: { ...formData, step4: data }
      });
      
      setCurrentStep(5);
      toast.success('Entity details saved');
    } catch (error) {
      toast.error('Failed to save entity details');
    } finally {
      setLoading(false);
    }
  };

  const handleStep5Submit = async (data) => {
    setLoading(true);
    try {
      // Add primary contact
      await clientsAPI.addPerson(clientId, {
        name: data.primary_contact_name,
        email: data.primary_contact_email,
        phone: data.primary_contact_phone,
        role: 'contact_person',
        is_primary: true,
      });
      
      setFormData({ ...formData, ...data });
      
      // Update onboarding status
      await clientsAPI.updateOnboarding(clientId, {
        current_step: 6,
        completed_steps: [1, 2, 3, 4, 5],
        data: { ...formData, step5: data },
      });
      
      setCurrentStep(6);
      toast.success('Contact added successfully');
    } catch (error) {
      toast.error('Failed to add contact');
    } finally {
      setLoading(false);
    }
  };

  const handleStep6Submit = (services) => {
    setFormData({ ...formData, services });
    setCurrentStep(7);
  };

  // Step 7 — Rental properties: save each entered property against the new client.
  const handleStep7Submit = async (data) => {
    setLoading(true);
    try {
      const properties = data.has_rentals ? (data.rental_properties || []) : [];
      let saved = 0;
      for (const p of properties) {
        if (!p.property_address) continue;  // skip incomplete rows
        await api.post('/api/rental-properties', {
          client_id: clientId,
          property_address: p.property_address,
          property_type: p.property_type || 'residential',
          ownership_structure: p.ownership_structure || 'individual',
          ownership_percentage: p.ownership_percentage ? parseFloat(p.ownership_percentage) : 100,
          purchase_date: p.purchase_date || undefined,
          purchase_price: p.purchase_price ? parseFloat(p.purchase_price) : undefined,
        });
        saved += 1;
      }
      setFormData({ ...formData, ...data });
      await clientsAPI.updateOnboarding(clientId, {
        current_step: 8,
        completed_steps: [1, 2, 3, 4, 5, 6, 7],
        data: { ...formData, step7: data },
      });
      setCurrentStep(8);
      toast.success(saved > 0 ? `${saved} rental ${saved === 1 ? 'property' : 'properties'} saved` : 'Rental step complete');
    } catch (error) {
      toast.error('Failed to save rental properties');
    } finally {
      setLoading(false);
    }
  };

  const handleStep8Submit = async () => {
    setLoading(true);
    try {
      // Update client with services
      await clientsAPI.update(clientId, {
        services: formData.services,
      });

      // Mark onboarding as complete
      await clientsAPI.updateOnboarding(clientId, {
        current_step: 8,
        completed_steps: [1, 2, 3, 4, 5, 6, 7, 8],
        data: formData,
      });

      toast.success('Onboarding completed successfully!');
      navigate(`/clients/${clientId}`);
    } catch (error) {
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/clients')}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Clients
            </Button>
            <div className="text-sm text-muted-foreground">
              Step {currentPosition} of {totalSteps}
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-foreground mb-2" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
            Client Onboarding
          </h1>
          <p className="text-muted-foreground mb-4">Complete these steps to onboard your new client</p>
          
          <Progress value={progress} className="h-3" data-testid="onboarding-progress" />
        </div>

        {/* Step Indicators */}
        <div className="flex items-center justify-between mb-8 overflow-x-auto pb-4">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all ${
                    step.number < currentStep
                      ? 'bg-primary text-white'
                      : step.number === currentStep
                      ? 'bg-accent text-primary border-2 border-primary'
                      : 'bg-muted text-muted-foreground/70'
                  }`}
                >
                  {step.number < currentStep ? (
                    <CheckCircle className="w-6 h-6" />
                  ) : (
                    <step.icon className="w-6 h-6" />
                  )}
                </div>
                <div className="mt-2 text-center hidden md:block">
                  <p className="text-xs font-medium text-foreground/90">{step.title}</p>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`w-16 lg:w-24 h-1 mx-2 ${
                    step.number < currentStep ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card>
          <CardHeader>
            <CardTitle style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
              {currentStepObj?.title}
            </CardTitle>
            <CardDescription>{currentStepObj?.description}</CardDescription>
          </CardHeader>
          <CardContent>
            {currentStep === 1 && (
              <Step1Form form={step1Form} onSubmit={handleStep1Submit} loading={loading} />
            )}
            {currentStep === 2 && (
              <Step2Form form={step2Form} onSubmit={handleStep2Submit} onBack={() => setCurrentStep(1)} loading={loading} entityType={formData.entity_type} />
            )}
            {currentStep === 3 && (
              <Step3FinancialForm 
                formData={formData}
                onSubmit={handleStep3Submit}
                onBack={() => setCurrentStep(2)}
                loading={loading}
              />
            )}
            {currentStep === 4 && (
              <Step4EntityForm
                formData={formData}
                entityType={formData.entity_type}
                onSubmit={handleStep4Submit}
                onBack={() => setCurrentStep(3)}
                loading={loading}
              />
            )}
            {currentStep === 5 && (
              <Step5ContactsForm form={step3Form} onSubmit={handleStep5Submit} onBack={() => setCurrentStep(isIndividual ? 3 : 4)} loading={loading} isIndividual={isIndividual} individualName={formData.display_name} />
            )}
            {currentStep === 6 && (
              <Step6ServicesForm 
                formData={formData} 
                onSubmit={handleStep6Submit} 
                onBack={() => setCurrentStep(5)}
              />
            )}
            {currentStep === 7 && (
              <Step7RentalForm
                formData={formData}
                onSubmit={handleStep7Submit}
                onBack={() => setCurrentStep(6)}
                loading={loading}
              />
            )}
            {currentStep === 8 && (
              <Step7Review
                formData={formData}
                onSubmit={handleStep8Submit}
                onBack={() => setCurrentStep(7)}
                loading={loading}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Step 1: Basic Information
function Step1Form({ form, onSubmit, loading }) {
  const entityType = form.watch('entity_type');
  const isIndividual = entityType === 'individual' || entityType === 'sole_trader';

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      {/* Entity type first — it determines which name fields to show */}
      <div className="space-y-2">
        <Label htmlFor="entity_type">Business Type *</Label>
        <Select
          value={form.watch('entity_type')}
          onValueChange={(value) => form.setValue('entity_type', value, { shouldValidate: true })}
        >
          <SelectTrigger id="entity_type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="individual">Individual (Personal Tax)</SelectItem>
            <SelectItem value="sole_trader">Sole Trader</SelectItem>
            <SelectItem value="company">Company (Pty Ltd)</SelectItem>
            <SelectItem value="trust">Trust (Family/Unit/Discretionary)</SelectItem>
            <SelectItem value="smsf">SMSF (Self-Managed Super Fund)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Select the legal structure - this determines which fields you'll need to complete
        </p>
      </div>

      {isIndividual ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="first_name">First Name *</Label>
            <Input id="first_name" placeholder="John" {...form.register('first_name')} data-testid="first-name-input" />
            {form.formState.errors.first_name && (
              <p className="text-sm text-red-500">{form.formState.errors.first_name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="last_name">Last Name *</Label>
            <Input id="last_name" placeholder="Smith" {...form.register('last_name')} data-testid="last-name-input" />
            {form.formState.errors.last_name && (
              <p className="text-sm text-red-500">{form.formState.errors.last_name.message}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="business_name">Business Name *</Label>
          <Input id="business_name" placeholder="e.g., ABC Pty Ltd" {...form.register('business_name')} data-testid="business-name-input" />
          {form.formState.errors.business_name && (
            <p className="text-sm text-red-500">{form.formState.errors.business_name.message}</p>
          )}
        </div>
      )}

      <div className="flex justify-end pt-4">
        <Button
          type="submit"
          className="bg-primary hover:bg-primary/90"
          disabled={loading}
          data-testid="step1-next-button"
        >
          {loading ? 'Creating...' : 'Next'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );
}

// Step 2: Business Details (Conditional based on entity type)
function Step2Form({ form, onSubmit, onBack, loading, entityType }) {
  const gstRegistered = form.watch('gst_registered');
  
  // Determine which fields to show based on entity type
  const showACN = entityType === 'company';
  const showTFN = true; // Show for all entity types (trustees, directors, individuals)
  const showDOB = entityType === 'individual';
  const showABN = entityType !== 'individual'; // All except individual

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      {/* ABN - for all except individuals */}
      {showABN && (
        <div className="space-y-2">
          <Label htmlFor="abn">ABN (Australian Business Number)</Label>
          <Input
            id="abn"
            placeholder="12 345 678 901 (11 digits)"
            {...form.register('abn')}
            maxLength={11}
            className="font-mono"
            data-testid="abn-input"
          />
          {form.formState.errors.abn && (
            <p className="text-sm text-red-500">{form.formState.errors.abn.message}</p>
          )}
          <p className="text-xs text-muted-foreground">11 digits, no spaces</p>
        </div>
      )}

      {/* ACN - only for companies */}
      {showACN && (
        <div className="space-y-2">
          <Label htmlFor="acn">ACN (Australian Company Number) *</Label>
          <Input
            id="acn"
            placeholder="123 456 789 (9 digits)"
            {...form.register('acn')}
            maxLength={9}
            className="font-mono"
            data-testid="acn-input"
          />
          {form.formState.errors.acn && (
            <p className="text-sm text-red-500">{form.formState.errors.acn.message}</p>
          )}
          <p className="text-xs text-muted-foreground">9 digits, no spaces (required for companies)</p>
        </div>
      )}

      {/* TFN - only for individuals and sole traders */}
      {showTFN && (
        <div className="space-y-2">
          <Label htmlFor="tfn">TFN (Tax File Number)</Label>
          <Input
            id="tfn"
            placeholder="123 456 789 (9 digits)"
            {...form.register('tfn')}
            maxLength={9}
            className="font-mono"
            data-testid="tfn-input"
          />
          {form.formState.errors.tfn && (
            <p className="text-sm text-red-500">{form.formState.errors.tfn.message}</p>
          )}
          <p className="text-xs text-muted-foreground">9 digits, no spaces</p>
        </div>
      )}

      {/* Date of Birth - only for individuals */}
      {showDOB && (
        <div className="space-y-2">
          <Label htmlFor="date_of_birth">Date of Birth</Label>
          <Input
            id="date_of_birth"
            type="date"
            {...form.register('date_of_birth')}
            data-testid="dob-input"
          />
          {form.formState.errors.date_of_birth && (
            <p className="text-sm text-red-500">{form.formState.errors.date_of_birth.message}</p>
          )}
        </div>
      )}

      {/* Address - for all */}
      <div className="space-y-2">
        <Label htmlFor="address">
          {entityType === 'individual' ? 'Residential Address' : 'Business Address'}
        </Label>
        <Input
          id="address"
          placeholder="123 Main St, Sydney NSW 2000"
          {...form.register('address')}
          data-testid="address-input"
        />
        {form.formState.errors.address && (
          <p className="text-sm text-red-500">{form.formState.errors.address.message}</p>
        )}
      </div>

      <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-lg">
        <Checkbox
          id="gst_registered"
          checked={form.watch('gst_registered')}
          onCheckedChange={(checked) => form.setValue('gst_registered', checked)}
          data-testid="gst-registered-checkbox"
        />
        <Label htmlFor="gst_registered" className="cursor-pointer">
          GST Registered
        </Label>
      </div>

      {gstRegistered && (
        <div className="space-y-2">
          <Label htmlFor="bas_cycle">BAS Cycle</Label>
          <Select
            value={form.watch('bas_cycle')}
            onValueChange={(value) => form.setValue('bas_cycle', value)}
          >
            <SelectTrigger data-testid="bas-cycle-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="annually">Annually</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          type="submit"
          className="bg-primary hover:bg-primary/90"
          disabled={loading}
          data-testid="step2-next-button"
        >
          {loading ? 'Saving...' : 'Next'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );
}

// Step 3: Financial Details (NEW)
function Step3FinancialForm({ formData, onSubmit, onBack, loading }) {
  const [bankAccounts, setBankAccounts] = useState(formData.bank_accounts || []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ bank_accounts: bankAccounts });
  };

  const bankFieldConfig = [
    { name: 'account_name', label: 'Account Name', required: true },
    { name: 'bsb', label: 'BSB', required: true, maxLength: 6, placeholder: '123-456' },
    { name: 'account_number', label: 'Account Number', required: true },
    { name: 'account_type', label: 'Type', placeholder: 'business/trust/operating' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <RepeatableFieldArray
        label="Bank Accounts"
        items={bankAccounts}
        onItemsChange={setBankAccounts}
        fieldConfig={bankFieldConfig}
      />

      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={loading}>
          {loading ? 'Saving...' : 'Next'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );
}

// Step 4: Entity-Specific People (NEW)
function Step4EntityForm({ formData, entityType, onSubmit, onBack, loading }) {
  const [directors, setDirectors] = useState(formData.directors || []);
  const [shareholders, setShareholders] = useState(formData.shareholders || []);
  const [trustees, setTrustees] = useState(formData.trustees || []);
  const [beneficiaries, setBeneficiaries] = useState(formData.beneficiaries || []);
  const [smsfMembers, setSmsfMembers] = useState(formData.smsf_members || []);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const data = {};
    if (entityType === 'company') {
      data.directors = directors;
      data.shareholders = shareholders;
    } else if (entityType === 'trust') {
      data.trustees = trustees;
      data.beneficiaries = beneficiaries;
    } else if (entityType === 'smsf') {
      data.smsf_members = smsfMembers;
      data.trustees = trustees;
    }
    
    onSubmit(data);
  };

  const directorFields = [
    { name: 'name', label: 'Full Name', required: true },
    { name: 'date_of_birth', label: 'Date of Birth', type: 'date' },
    { name: 'tfn', label: 'TFN', maxLength: 9, placeholder: '123456789' },
    { name: 'address', label: 'Address' },
    { name: 'appointment_date', label: 'Appointment Date', type: 'date' }
  ];

  const shareholderFields = [
    { name: 'name', label: 'Shareholder Name', required: true },
    { name: 'shares_held', label: 'Shares Held', type: 'number', required: true },
    { name: 'percentage_ownership', label: 'Ownership %', type: 'number', required: true },
    { name: 'share_class', label: 'Share Class', placeholder: 'Ordinary' }
  ];

  const trusteeFields = [
    { name: 'name', label: 'Trustee Name', required: true },
    { name: 'trustee_type', label: 'Type', placeholder: 'individual/corporate' },
    { name: 'tfn_or_abn', label: 'TFN or ABN', maxLength: 11 }
  ];

  const beneficiaryFields = [
    { name: 'name', label: 'Beneficiary Name', required: true },
    { name: 'relationship', label: 'Relationship' },
    { name: 'distribution_percentage', label: 'Distribution %', type: 'number' }
  ];

  const smsfMemberFields = [
    { name: 'name', label: 'Member Name', required: true },
    { name: 'date_of_birth', label: 'Date of Birth', type: 'date' },
    { name: 'tfn', label: 'TFN', maxLength: 9 },
    { name: 'member_number', label: 'Member Number' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {entityType === 'company' && (
        <>
          <RepeatableFieldArray
            label="Directors"
            items={directors}
            onItemsChange={setDirectors}
            fieldConfig={directorFields}
          />
          <RepeatableFieldArray
            label="Shareholders"
            items={shareholders}
            onItemsChange={setShareholders}
            fieldConfig={shareholderFields}
          />
        </>
      )}

      {entityType === 'trust' && (
        <>
          <RepeatableFieldArray
            label="Trustees"
            items={trustees}
            onItemsChange={setTrustees}
            fieldConfig={trusteeFields}
          />
          <RepeatableFieldArray
            label="Beneficiaries"
            items={beneficiaries}
            onItemsChange={setBeneficiaries}
            fieldConfig={beneficiaryFields}
          />
        </>
      )}

      {entityType === 'smsf' && (
        <>
          <RepeatableFieldArray
            label="SMSF Members"
            items={smsfMembers}
            onItemsChange={setSmsfMembers}
            fieldConfig={smsfMemberFields}
          />
          <RepeatableFieldArray
            label="Trustees"
            items={trustees}
            onItemsChange={setTrustees}
            fieldConfig={trusteeFields}
          />
        </>
      )}

      {(entityType === 'individual' || entityType === 'sole_trader') && (
        <div className="p-6 bg-muted/50 rounded-lg text-center">
          <p className="text-sm text-muted-foreground">
            No additional entity details required for {entityType === 'individual' ? 'individuals' : 'sole traders'}.
          </p>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={loading}>
          {loading ? 'Saving...' : 'Next'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );
}

// Step 5: Key Contacts (was Step 3)
function Step5ContactsForm({ form, onSubmit, onBack, loading, isIndividual, individualName }) {
  // For individuals we already captured their name in Step 1 (First + Last),
  // so don't ask again — reuse it as the primary contact name and only collect
  // email + phone here.
  useEffect(() => {
    if (isIndividual && individualName) {
      form.setValue('primary_contact_name', individualName, { shouldValidate: true });
    }
  }, [isIndividual, individualName]);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <h3 className="font-semibold text-foreground">Primary Contact</h3>

        {isIndividual ? (
          <div className="space-y-2">
            <Label>Full Name</Label>
            <div className="flex items-center px-3 py-2 border rounded-md bg-muted/50 text-foreground/90" data-testid="contact-name-prefilled">
              {individualName || '—'}
            </div>
            <p className="text-xs text-muted-foreground">
              Using the name from Basic Information. Just add an email and phone below.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="primary_contact_name">Full Name *</Label>
            <Input
              id="primary_contact_name"
              placeholder="John Smith"
              {...form.register('primary_contact_name')}
              data-testid="contact-name-input"
            />
            {form.formState.errors.primary_contact_name && (
              <p className="text-sm text-red-500">{form.formState.errors.primary_contact_name.message}</p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="primary_contact_email">Email *</Label>
          <Input
            id="primary_contact_email"
            type="email"
            placeholder="john@example.com"
            {...form.register('primary_contact_email')}
            data-testid="contact-email-input"
          />
          {form.formState.errors.primary_contact_email && (
            <p className="text-sm text-red-500">{form.formState.errors.primary_contact_email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="primary_contact_phone">Phone *</Label>
          <Input
            id="primary_contact_phone"
            placeholder="0400 000 000"
            {...form.register('primary_contact_phone')}
            data-testid="contact-phone-input"
          />
          {form.formState.errors.primary_contact_phone && (
            <p className="text-sm text-red-500">{form.formState.errors.primary_contact_phone.message}</p>
          )}
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          type="submit"
          className="bg-primary hover:bg-primary/90"
          disabled={loading}
          data-testid="step3-next-button"
        >
          {loading ? 'Saving...' : 'Next'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );
}

// Step 6: Services Selection (was Step 4)
function Step6ServicesForm({ formData, onSubmit, onBack }) {
  const [selectedServices, setSelectedServices] = useState(formData.services || []);

  const services = [
    { id: 'bas', label: 'BAS Preparation & Lodgment', description: 'Quarterly business activity statements' },
    { id: 'ias', label: 'IAS Preparation', description: 'Instalment activity statements' },
    { id: 'tax_return', label: 'Tax Return Preparation', description: 'Annual tax returns' },
    { id: 'payroll', label: 'Payroll Services', description: 'PAYG withholding and STP' },
    { id: 'fbt', label: 'FBT Returns', description: 'Fringe benefits tax' },
    { id: 'smsf', label: 'SMSF Administration', description: 'Self-managed super fund compliance' },
    { id: 'bookkeeping', label: 'Bookkeeping', description: 'Monthly bookkeeping services' },
    { id: 'advisory', label: 'Tax Advisory', description: 'Strategic tax planning' },
  ];

  const toggleService = (serviceId) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]
    );
  };

  const handleNext = () => {
    onSubmit(selectedServices);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services.map((service) => (
          <Label
            key={service.id}
            htmlFor={service.id}
            className="flex items-start space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
          >
            <Checkbox
              id={service.id}
              checked={selectedServices.includes(service.id)}
              onCheckedChange={() => toggleService(service.id)}
              data-testid={`service-${service.id}`}
            />
            <div>
              <div className="font-medium text-foreground">{service.label}</div>
              <div className="text-sm text-muted-foreground">{service.description}</div>
            </div>
          </Label>
        ))}
      </div>

      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          type="button"
          onClick={handleNext}
          className="bg-primary hover:bg-primary/90"
          data-testid="step4-next-button"
        >
          Next
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

// Step 7: Rental Properties (optional)
function Step7RentalForm({ formData, onSubmit, onBack, loading }) {
  const [hasRentals, setHasRentals] = useState(formData.has_rentals || false);
  const [properties, setProperties] = useState(formData.rental_properties || []);

  const rentalFields = [
    { name: 'property_address', label: 'Property Address', required: true, placeholder: '123 Main St, Sydney NSW 2000' },
    { name: 'property_type', label: 'Property Type', placeholder: 'residential / commercial' },
    { name: 'ownership_structure', label: 'Ownership', placeholder: 'individual / company / trust / smsf' },
    { name: 'ownership_percentage', label: 'Ownership %', type: 'number', placeholder: '100' },
    { name: 'purchase_date', label: 'Purchase Date', type: 'date' },
    { name: 'purchase_price', label: 'Purchase Price', type: 'number', placeholder: '750000' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ has_rentals: hasRentals, rental_properties: hasRentals ? properties : [] });
  };

  // When they tick "yes", show one empty property straight away so the form is obvious.
  const toggleHasRentals = (checked) => {
    setHasRentals(!!checked);
    if (checked && properties.length === 0) {
      setProperties([Object.fromEntries(rentalFields.map((f) => [f.name, '']))]);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-lg">
        <Checkbox
          id="has_rentals"
          checked={hasRentals}
          onCheckedChange={toggleHasRentals}
          data-testid="has-rentals-checkbox"
        />
        <Label htmlFor="has_rentals" className="cursor-pointer">
          Does this client have rental properties?
        </Label>
      </div>

      {hasRentals && (
        <div className="space-y-2">
          <RepeatableFieldArray
            label="Rental Properties"
            items={properties}
            onItemsChange={setProperties}
            fieldConfig={rentalFields}
            addLabel="Add Another Property"
          />
          <p className="text-xs text-muted-foreground">
            Add as many properties as the client owns. These save to the Rental Properties
            dashboard, where income and expenses can be added later.
          </p>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={loading} data-testid="step7-next-button">
          {loading ? 'Saving...' : 'Next'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );
}

// Step 8: Review & Complete
function Step7Review({ formData, onSubmit, onBack, loading }) {
  const entityTypeLabels = {
    company: 'Company',
    trust: 'Trust',
    smsf: 'SMSF',
    sole_trader: 'Sole Trader',
    individual: 'Individual',
  };

  const serviceLabels = {
    bas: 'BAS Preparation & Lodgment',
    ias: 'IAS Preparation',
    tax_return: 'Tax Return Preparation',
    payroll: 'Payroll Services',
    fbt: 'FBT Returns',
    smsf: 'SMSF Administration',
    bookkeeping: 'Bookkeeping',
    advisory: 'Tax Advisory',
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        {/* Basic Information */}
        <div className="p-4 bg-muted/50 rounded-lg">
          <h3 className="font-semibold text-foreground mb-3">Basic Information</h3>
          <dl className="space-y-2">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Client Name:</dt>
              <dd className="font-medium">{formData.display_name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Business Type:</dt>
              <dd className="font-medium">{entityTypeLabels[formData.entity_type]}</dd>
            </div>
          </dl>
        </div>

        {/* Business Details */}
        <div className="p-4 bg-muted/50 rounded-lg">
          <h3 className="font-semibold text-foreground mb-3">Business Details</h3>
          <dl className="space-y-2">
            {formData.abn && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">ABN:</dt>
                <dd className="font-medium font-mono">{formData.abn}</dd>
              </div>
            )}
            {formData.acn && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">ACN:</dt>
                <dd className="font-medium font-mono">{formData.acn}</dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-muted-foreground">GST Registered:</dt>
              <dd className="font-medium">{formData.gst_registered ? 'Yes' : 'No'}</dd>
            </div>
            {formData.gst_registered && formData.bas_cycle && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">BAS Cycle:</dt>
                <dd className="font-medium capitalize">{formData.bas_cycle}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Primary Contact */}
        <div className="p-4 bg-muted/50 rounded-lg">
          <h3 className="font-semibold text-foreground mb-3">Primary Contact</h3>
          <dl className="space-y-2">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Name:</dt>
              <dd className="font-medium">{formData.primary_contact_name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Email:</dt>
              <dd className="font-medium">{formData.primary_contact_email}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Phone:</dt>
              <dd className="font-medium">{formData.primary_contact_phone}</dd>
            </div>
          </dl>
        </div>

        {/* Services */}
        {formData.services && formData.services.length > 0 && (
          <div className="p-4 bg-muted/50 rounded-lg">
            <h3 className="font-semibold text-foreground mb-3">Selected Services</h3>
            <ul className="space-y-2">
              {formData.services.map((serviceId) => (
                <li key={serviceId} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span>{serviceLabels[serviceId]}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Rental Properties */}
        {formData.has_rentals && formData.rental_properties && formData.rental_properties.length > 0 && (
          <div className="p-4 bg-muted/50 rounded-lg">
            <h3 className="font-semibold text-foreground mb-3">Rental Properties</h3>
            <ul className="space-y-2">
              {formData.rental_properties.filter((p) => p.property_address).map((p, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Home className="w-4 h-4 text-primary" />
                  <span>{p.property_address} {p.property_type ? `(${p.property_type})` : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          className="bg-primary hover:bg-primary/90"
          disabled={loading}
          data-testid="complete-onboarding-button"
        >
          {loading ? 'Completing...' : 'Complete Onboarding'}
          <CheckCircle className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
